/**
 * The Contacts registry.
 *
 * Winnie, 15 September: "ABN lookup should check the Contacts registry first, before calling
 * the external ABN Lookup API — if the supplier's already a known Contact, use the stored
 * name/details directly; only hit the external API for a genuinely new ABN. This also means a
 * successful external lookup should offer to save the result as a new Contact, so the registry
 * grows from real usage instead of needing to be populated by hand. [Buyer] fields should
 * similarly check against Contacts, so a repeat wholesale buyer doesn't need re-entering every
 * deal. Inflow seller details (Type 1–5) should create or match a Contact the same way."
 *
 * THE ONE RULE WORTH READING BEFORE CHANGING ANYTHING HERE.
 *
 * A registry hit is allowed to stand in for an ABR call only when the registry knows whether
 * the entity is GST registered. That single field decides the Division 66 margin-scheme
 * treatment on an inflow deal, and the margin scheme decides what goes on a BAS. A contact
 * added by hand in Admin has no GST status — nobody typed one, because the form does not ask —
 * so answering an ABN lookup from that record would quietly turn "we do not know" into "not
 * registered", which is the more expensive of the two possible wrong answers. So
 * `lookupFromRegistry` returns a hit only when `gst_registered` is not null, which in practice
 * means the record was created or refreshed from a real ABR answer. Everything else still
 * benefits from the registry — the name auto-fills, the record is found, the buyer does not
 * need re-entering — it just does not skip the ABR.
 */
import { supabase } from '../lib/supabase'
import { isValidAbn } from '../lib/abn'

export type ContactType = 'Buyer' | 'Seller' | 'Supplier' | 'Dealer'
export const CONTACT_TYPES: ContactType[] = ['Buyer', 'Seller', 'Supplier', 'Dealer']

export interface Contact {
  id: string
  contact_number: string | null
  name: string
  legal_name: string | null
  abn: string | null
  address: string | null
  phone: string | null
  email: string | null
  contact_type: ContactType[]
  bank_account_name: string | null
  bank_account_number: string | null
  status: 'Active' | 'Inactive'
  gst_registered: boolean | null
  abn_status: string | null
  abn_checked_at: string | null
  notes: string | null
  created_at?: string
  updated_at?: string
}

export interface ContactInput {
  name: string
  legal_name?: string | null
  abn?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
  contact_type?: ContactType[]
  bank_account_name?: string | null
  bank_account_number?: string | null
  status?: 'Active' | 'Inactive'
  gst_registered?: boolean | null
  abn_status?: string | null
  notes?: string | null
}

/** Digits only. Contacts are stored unformatted so "51 824 753 556" and "51824753556" match. */
export const abnDigits = (abn?: string | null): string => String(abn || '').replace(/\D/g, '')

const SELECT = '*'

export const contactsService = {
  async list(opts?: { status?: 'Active' | 'Inactive'; type?: ContactType; search?: string }): Promise<Contact[]> {
    let q = supabase.from('contacts').select(SELECT).order('name')
    if (opts?.status) q = q.eq('status', opts.status)
    if (opts?.type) q = q.contains('contact_type', [opts.type])
    const term = (opts?.search || '').trim()
    if (term.length >= 2) {
      const like = `%${term}%`
      q = q.or(`name.ilike.${like},legal_name.ilike.${like},abn.ilike.${like},email.ilike.${like},contact_number.ilike.${like}`)
    }
    const { data, error } = await q
    if (error) throw error
    return (data || []) as Contact[]
  },

  /** One contact by ABN, however the ABN was spaced. Inactive records are not returned. */
  async findByAbn(abn: string): Promise<Contact | null> {
    const digits = abnDigits(abn)
    if (!digits) return null
    const { data, error } = await supabase
      .from('contacts')
      .select(SELECT)
      .eq('abn', digits)
      .eq('status', 'Active')
      .limit(1)
    if (error) throw error
    return ((data || [])[0] as Contact) || null
  },

  /**
   * The registry's answer to an ABN lookup, or null to mean "ask the ABR".
   *
   * See the note at the top of this file: a record with no recorded GST status is a hit for
   * the name and not a substitute for the register.
   */
  async lookupFromRegistry(abn: string): Promise<Contact | null> {
    const c = await contactsService.findByAbn(abn)
    if (!c) return null
    if (c.gst_registered === null || c.gst_registered === undefined) return null
    if (!c.legal_name && !c.name) return null
    return c
  },

  async create(input: ContactInput): Promise<Contact> {
    const { data, error } = await supabase
      .from('contacts')
      .insert([
        {
          name: (input.name || '').trim(),
          legal_name: input.legal_name?.trim() || null,
          abn: abnDigits(input.abn) || null,
          address: input.address?.trim() || null,
          phone: input.phone?.trim() || null,
          email: input.email?.trim() || null,
          contact_type: input.contact_type || [],
          bank_account_name: input.bank_account_name?.trim() || null,
          bank_account_number: input.bank_account_number?.trim() || null,
          status: input.status || 'Active',
          gst_registered: input.gst_registered ?? null,
          abn_status: input.abn_status || null,
          abn_checked_at: input.gst_registered === null || input.gst_registered === undefined ? null : new Date().toISOString(),
          notes: input.notes || null,
        },
      ])
      .select(SELECT)
      .single()
    if (error) throw error
    return data as Contact
  },

  async update(id: string, patch: Partial<ContactInput>): Promise<Contact> {
    const body: Record<string, unknown> = { ...patch }
    if ('abn' in patch) body.abn = abnDigits(patch.abn) || null
    if ('gst_registered' in patch && patch.gst_registered !== null && patch.gst_registered !== undefined) {
      body.abn_checked_at = new Date().toISOString()
    }
    const { data, error } = await supabase.from('contacts').update(body).eq('id', id).select(SELECT).single()
    if (error) throw error
    return data as Contact
  },

  /**
   * Add a type to an existing contact without disturbing the ones already on it.
   *
   * A licensed dealer is routinely both the seller on a morning deal and the buyer on an
   * afternoon one. Overwriting the array on the second deal would lose the first fact.
   */
  async addType(contact: Contact, type: ContactType): Promise<Contact> {
    const have = contact.contact_type || []
    if (have.includes(type)) return contact
    return contactsService.update(contact.id, { contact_type: [...have, type] })
  },

  /**
   * Find a contact for this ABN, or create one. Used by the seller and buyer flows so the
   * registry grows from real deals instead of by hand.
   *
   * Never throws into a deal: a registry that is unreachable must not stop a deal being
   * created. Returns null when it could not be done.
   */
  async matchOrCreate(
    input: ContactInput & { contact_type: ContactType[] }
  ): Promise<Contact | null> {
    try {
      const digits = abnDigits(input.abn)
      if (digits && isValidAbn(digits)) {
        const existing = await contactsService.findByAbn(digits)
        if (existing) {
          let out = existing
          for (const t of input.contact_type) out = await contactsService.addType(out, t)
          /* Fill the blanks a later deal learned, never overwrite what is already recorded. */
          const fill: Partial<ContactInput> = {}
          if (!out.legal_name && input.legal_name) fill.legal_name = input.legal_name
          if (!out.email && input.email) fill.email = input.email
          if (!out.phone && input.phone) fill.phone = input.phone
          if (!out.address && input.address) fill.address = input.address
          if (Object.keys(fill).length) out = await contactsService.update(out.id, fill)
          return out
        }
      }
      if (!(input.name || '').trim()) return null
      return await contactsService.create(input)
    } catch (e) {
      /* A registry write is never allowed to fail a deal. */
      console.warn('Contacts: could not match or create a contact', e)
      return null
    }
  },

  /** Contacts that can stand in for a wholesale buyer, for the buyer search. */
  async searchByType(type: ContactType, term: string, limit = 8): Promise<Contact[]> {
    const q = (term || '').trim()
    if (q.length < 2) return []
    const like = `%${q}%`
    const { data, error } = await supabase
      .from('contacts')
      .select(SELECT)
      .eq('status', 'Active')
      .contains('contact_type', [type])
      .or(`name.ilike.${like},legal_name.ilike.${like},abn.ilike.${like},email.ilike.${like}`)
      .order('name')
      .limit(limit)
    if (error) throw error
    return (data || []) as Contact[]
  },
}
